/**
 * WHAT HOUR IT IS SOMEWHERE ELSE, AND WHEN THAT PLACE'S DAY STARTED.
 *
 * Anthony, 2026-09-18: "fix the timezone thing so windows use
 * accounts.timezone."
 *
 * `server/tasks/guard.ts` pinned both of these to America/Los_Angeles and
 * gated every automated message on them. The hours in `automations` are
 * bare numbers, so the zone they are read in is what decides what a
 * window MEANS: NYIA's stored 7-21 was really answering New York at ten in
 * the morning and texting it until midnight.
 *
 * ─── PURE, AND IN src/domain, SO IT CAN BE TESTED AT ALL ───────────────
 *
 * No imports. guard.ts reaches server/env.ts, which throws when
 * SUPABASE_URL is unset, so a test of the arithmetic could not import the
 * arithmetic. That is the same reason commission.ts, place.ts and trade.ts
 * live here. `server/desk/zone.ts` is the half that knows how to ask the
 * database and it is the only half that needs an environment.
 *
 * ─── THE ONE COPY OF THIS ARITHMETIC ───────────────────────────────────
 *
 * src/domain/shopday.ts used to carry a second copy for the browser, under
 * a header explaining why it was Pacific: "Supercanic is one shop in one
 * zone and always has been." On 2026-09-18 that was rewritten to call this
 * module, so the offset maths exists here and nowhere else. shopday.ts is
 * now the calendar-grid layer on top of it -- squares on a board, keys for
 * a map -- and it owns no offsets of its own.
 *
 * If you are about to write `new Intl.DateTimeFormat` with a timeZone and
 * then do arithmetic on the result, it belongs in this file.
 */

/**
 * How far `zone` sits from UTC at that instant, in milliseconds. Negative
 * in the Americas.
 */
export function offsetMs(zone: string, at: Date): number {
  const p: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat("en-CA", {
    timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(at)) p[part.type] = part.value;
  return Date.UTC(
    Number(p["year"]), Number(p["month"]) - 1, Number(p["day"]),
    Number(p["hour"]), Number(p["minute"]), Number(p["second"]),
  ) - at.getTime();
}

/**
 * The hour in `zone`, as a decimal so 8:30pm is 20.5.
 *
 * hourCycle "h23", NEVER hour12:false. server/intake/clock.ts carries the
 * incident: `hour12:false` renders midnight as "24" on some ICU builds.
 * The function this replaces had no modulo on it, so on such a build it
 * returned 24.x at midnight -- outside every window there is, including
 * the `0-24` one Wrench Services runs its text desk on.
 */
export function hourIn(zone: string, at = new Date()): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone, hour: "numeric", minute: "numeric", hourCycle: "h23",
  }).formatToParts(at);
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0) % 24;
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return h + m / 60;
}

/**
 * Is `hour` (from hourIn, so 23.38 at 11:23 pm) inside a send window of
 * `start` to `end`, as the automation row stores them.
 *
 * TWO MEANINGS LIVE IN THOSE NUMBERS, AND THIS IS WHERE THEY MEET.
 *
 * The automations screen reads 7-21 as "7:00 to 21:00", so the end is a
 * boundary: 21:00 is the last moment, 21:01 is out. But round the clock
 * has been written `0-23` on lead_alert, form-backstop and esign since
 * they shipped (and `0-24` on the text desk), meaning every hour. The old
 * check was `hour > end`, and hourIn returns minutes as a fraction, so
 * 23.38 > 23 held every alert from 11:00 to 11:59 pm on shops that are
 * meant never to be held. Swift, 2026-10-09 06:23 UTC, 11:23 pm Pacific.
 *
 * So a window that starts at 0 and ends at 23 or later is round the clock
 * and is never closed. Every other window shuts at end:00 exactly.
 */
export function inSendWindow(hour: number, start: number, end: number): boolean {
  if (start <= 0 && end >= 23) return true;
  return hour >= start && hour < end;
}

/**
 * The calendar date in `zone` at that instant, as "2026-09-18".
 *
 * en-CA because it renders ISO order natively, which is the same trick
 * offsetMs above relies on.
 */
export const ymdIn = (zone: string, at = new Date()): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: zone }).format(at);

/**
 * The instant a wall-clock date -- "2026-09-18", no time, no offset --
 * begins in `zone`.
 *
 * TWO PASSES, because the offset has to be read AT the answer and not at
 * the guess. On a spring-forward morning the offset an hour before local
 * midnight is not the offset at it, and a one-pass version lands an hour
 * out twice a year -- which is a cap that resets at the wrong time on
 * exactly the two days nobody is looking.
 *
 * Split out of dayStartIn so that src/domain/shopday.ts, which starts from
 * a calendar square the person tapped rather than from an instant, can
 * reach the same two passes instead of writing a third copy of them.
 */
export function startOfWallDay(zone: string, ymd: string): Date {
  return wallClockIn(zone, `${ymd}T00:00:00`);
}

/**
 * The instant a wall clock in `zone` reads `local` ("2026-09-27T12:00").
 * The same two passes as startOfWallDay, for any time of day rather than
 * only midnight.
 */
export function wallClockIn(zone: string, local: string): Date {
  const wall = Date.parse(`${local}Z`);               // the wall clock, pretending UTC
  const once = new Date(wall - offsetMs(zone, new Date(wall)));
  return new Date(wall - offsetMs(zone, once));
}

/**
 * A booking time as the text desk hands it over, read in the shop's zone.
 *
 * Wrench Services, 2026-09-26: Matt texted "book a 2 hour diagnostic for
 * Sunday after 12pm" from Albuquerque. The tool told the model to write
 * the time with "the Pacific offset", so it did, and the reply read the
 * instant back in Pacific: "Sunday Jan 26, 11:00 AM". The hour was right
 * and the words were wrong, and Matt asked for his timezone to be changed.
 *
 * So the model now gives the wall clock alone and the offset comes from
 * the shop's own zone here. A string that still carries Z or an offset is
 * taken as the instant it names, so an older prompt cannot be misread.
 * Anything else is null, and the caller asks again.
 */
export function readBookingTime(zone: string, raw: string): Date | null {
  const s = raw.trim();
  if (/(Z|[+-]\d\d:?\d\d)$/i.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const m = /^(\d{4}-\d\d-\d\d)[T ](\d\d?):(\d\d)(?::(\d\d))?$/.exec(s);
  if (!m) return null;
  const local = `${m[1]}T${m[2]!.padStart(2, "0")}:${m[3]}:${m[4] ?? "00"}`;
  // Checked first: Intl throws on an invalid Date inside offsetMs.
  if (Number.isNaN(Date.parse(`${local}Z`))) return null;
  return wallClockIn(zone, local);
}

/** "Sat Sep 26 2026, 10:07 AM", the way a person in `zone` would say it. */
export const nowIn = (zone: string, at = new Date()): string =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: zone, weekday: "short", month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  }).format(at);

/**
 * Midnight in `zone`, as an instant, so a daily cap's day is that shop's
 * day rather than the server's.
 */
export const dayStartIn = (zone: string, at = new Date()): Date =>
  startOfWallDay(zone, ymdIn(zone, at));

export const PACIFIC = "America/Los_Angeles";

/**
 * A zone Intl will actually accept, or Pacific.
 *
 * A typo saved into accounts.timezone would otherwise throw inside
 * DateTimeFormat on the path that decides whether a customer hears from
 * the shop at all. Falling back to Pacific is the behavior every shop had
 * before this existed, so the worst case is the old bug rather than a new
 * silence.
 */
export function usableZone(zone: unknown): string {
  const z = String(zone ?? "").trim();
  if (!z) return PACIFIC;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: z });
    return z;
  } catch {
    return PACIFIC;
  }
}
